import { Directive, Injector } from '@angular/core';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { Content, ContentTypeEnum, IPagination } from 'lingo2-models';
import { isArray as _isArray } from 'lodash';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AbstractPane } from './abstract-pane';

@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class AbstractContentPane extends AbstractPane {
  public contentRoute = LibraryRouter.contentRouteUniversal;
  protected libRouter: LibraryRouter;
  protected readonly store: Store;

  protected constructor(
    protected readonly inject: Injector,
    public errorNotificationService?: ErrorNotificationService,
  ) {
    super(inject);
    this.store = inject.get(Store);

    this.libRouter = new LibraryRouter({});
    this.store
      .select(getLibraryRouteData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data) => {
          if (!data) {
            return;
          }
          this.libRouter.setBaseRoute(data.baseRoute);
          this.libRouter.setSubjects(data.subjectCodes);
          this.libRouter.setContentTypes(data.contentTypeCodes);
          if (!data.isDefaultOnlyLanguages) {
            this.libRouter.setLanguageCodes(data.languageCodes);
          }
          this.onLibraryRoute();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get content(): Content[] {
    if (!this._pane) {
      return [];
    }
    return this._pane?.payload__content || this._pane.content;
  }

  hasContent(): boolean {
    if (!this._pane) {
      return false;
    }
    return !!(this.content && this.content?.length);
  }

  load() {
    if (!this._pane) {
      return;
    }

    this.onLibraryRoute();

    // Content flow
    if (this._pane.contentFilter) {
      if (this._pane.loading) {
        // already under loading
        return;
      }
      if (this._pane.loaded) {
        // already loaded
        this.loadDataFinished(false);
        return;
      }
      this.loadContent();
      return;
    }

    // Any other flow
    return super.load();
  }

  protected onLibraryRoute() {
    if (this._pane) {
      this.addLink = ![undefined].includes(this._pane.routerUrlCreate)
        ? this._pane.routerUrlCreate
        : this.libRouter.getCreateLinkByPane(this._pane);
      this.seeAllLink = ![undefined, ''].includes(this._pane.routerUrlAll)
        ? this._pane.routerUrlAll
        : this.libRouter.getSeeAllLinkByPane(this._pane);
      this.titleLink = this.seeAllLink; // TODO ? отдельная ссылка
    } else {
      this.addLink = '';
      this.seeAllLink = '';
      this.titleLink = '';
    }

    this.blockTitle.add_link = this.addLink;
    this.blockTitle.see_all_link = this._pane?.infinity ? '' : this.seeAllLink;
    this.blockTitle.title_link = this.titleLink;

    this.markForCheck();
  }

  protected calculateTitle(): string | string[] {
    if (this._pane?.title) {
      return this._pane?.title;
    }
    if (this._pane?.title === 'none') {
      return '';
    } else {
      if (this._pane.contentFilter && this._pane.contentFilter.content_type) {
        const contentType: any = this._pane.contentFilter.content_type;
        if (_isArray(contentType)) {
          return contentType.map((ct) => 'content-types-title.' + ContentTypeEnum[ct]);
        } else {
          return 'content-types-title.' + ContentTypeEnum[contentType];
        }
      }
    }
    return super.calculateTitle();
  }

  protected loadContent() {
    if (!this._pane.loaded) {
      this._pane.loading = true;
      const pagination: IPagination = this._pane.pagination
        ? this._pane.pagination
        : {
            page: 1,
            pageSize: 10,
            total: 0,
            totalPages: 0,
          };
      const replaceFilter = this._pane.contentFilter;

      if (this._pane.payload__content) {
        this._loadedItemsCount = this._pane?.payload__content?.length;
        this.loadDataFinished(true);
      } else {
        if (!this.dataServices.contentService) {
          this.handleNoServiceError('contentService in Abstract Content Pane');
          return;
        }
        if (this._pane.contentFilter.related_to) {
          this.dataServices.contentService
            .getRelated(this._pane.contentFilter.related_to, this._pane.contentFilter, pagination)
            .pipe(
              switchMap((response) => this.extendUsersPaginated$(response, 'author_id', 'author', 'FOREVER')),
              takeUntil(this.destroyed$),
            )
            .subscribe({
              next: (contentResult) => this.paginatedContentLoader(contentResult, 'content'),
              error: (error) => {
                this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
              },
            });
        } else {
          this.dataServices.contentService
            .getContent(this._pane.contentFilter, pagination)
            .pipe(
              switchMap((response) => this.extendUsersPaginated$(response, 'author_id', 'author', 'FOREVER')),
              takeUntil(this.destroyed$),
            )
            .subscribe({
              next: (contentResult) => this.paginatedContentLoader(contentResult, 'content'),
              error: (error) => {
                this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
              },
            });
        }
      }
    } else {
      this.loadDataFinished(false);
    }
  }

  protected loadContentBff() {
    if (!this._pane.loaded) {
      this._pane.loading = true;
      const pagination: IPagination = this._pane.pagination
        ? this._pane.pagination
        : {
            page: 1,
            pageSize: 10,
            total: 0,
            totalPages: 0,
          };
      const replaceFilter = this._pane.contentFilter;
      this._loadedItemsCount = this._pane?.payload__content?.length;
      this.loadDataFinished(true);
    } else {
      this.loadDataFinished(false);
    }
  }
}
